import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  JOIN_ROOM,
  RECEIVE_MESSAGE,
  SEND_MESSAGE,
  SET_ACTIVE_ROOM,
} from "../../constants/chatHubMethods";
import HubConnector from "./HubConnector";
import { useDispatch, useSelector } from "react-redux";
import { addMessageToRoom } from "../../store/slices/messageRoomSlice";
import { apiSlice } from "../../store/api/apiSlice";
import * as signalR from "@microsoft/signalr";
import { normalizeChatMessage } from "../../utils/storedFile";

/** Birden fazla useHub örneği aynı SignalR listener'ı paylaşır */
const messageSubscribers = new Set();
const stateSubscribers = new Set();
let hubListenersBoundTo = null;
let lifecycleBoundTo = null;

const isChatHubPath = (path = "") =>
  /\/messages(\/|$|\?)/.test(path) ||
  /\/appointments\/(meeting-prepare|meeting)(\/|$|\?)/.test(path);

const notifyState = (state) => {
  stateSubscribers.forEach((fn) => {
    try {
      fn(state);
    } catch (err) {
      console.error("[useHub] state subscriber error:", err);
    }
  });
};

const dispatchToSubscribers = (senderId, messageJson) => {
  messageSubscribers.forEach((handler) => {
    try {
      handler(senderId, messageJson);
    } catch (err) {
      console.error("[useHub] subscriber error:", err);
    }
  });
};

const errorHandler = (error) => {
  console.error("ChatHub error:", error);
};

const bindSharedHubListeners = (connection) => {
  if (!connection || hubListenersBoundTo === connection) return;
  if (hubListenersBoundTo) {
    try {
      hubListenersBoundTo.off(RECEIVE_MESSAGE, dispatchToSubscribers);
      hubListenersBoundTo.off("ReceiveError", errorHandler);
    } catch {
      /* ignore */
    }
  }
  connection.off(RECEIVE_MESSAGE, dispatchToSubscribers);
  connection.off("ReceiveError", errorHandler);
  connection.on(RECEIVE_MESSAGE, dispatchToSubscribers);
  connection.on("ReceiveError", errorHandler);
  hubListenersBoundTo = connection;
};

/** Bağlantı başına bir kez lifecycle (onclose birikmesin) */
const bindConnectionLifecycle = (connection, getShouldKeepAlive, connector) => {
  if (!connection || lifecycleBoundTo === connection) return;
  lifecycleBoundTo = connection;

  let reconnectInFlight = false;

  const sync = () => notifyState(connection.state);

  connection.onclose(async () => {
    sync();
    if (!getShouldKeepAlive() || reconnectInFlight) return;
    reconnectInFlight = true;
    try {
      for (let attempts = 0; attempts < 5; attempts += 1) {
        if (!getShouldKeepAlive()) break;
        if (connection.state === signalR.HubConnectionState.Connected) break;
        const connected = await connector.startConnection();
        const live = connector.getConnection();
        if (connected || live?.state === signalR.HubConnectionState.Connected) {
          bindSharedHubListeners(live);
          notifyState(live.state);
          break;
        }
        await new Promise((r) => setTimeout(r, Math.min(1000 * (attempts + 1), 5000)));
      }
    } finally {
      reconnectInFlight = false;
    }
  });

  connection.onreconnecting(sync);
  connection.onreconnected(() => {
    bindSharedHubListeners(connection);
    sync();
    reconnectInFlight = false;
  });
};

const useHub = () => {
  const connector = HubConnector;
  const dispatch = useDispatch();
  const { currentPath } = useSelector((state) => state.app);
  const [newMessage, setNewMessage] = useState(null);
  const [isMessageRecieved, setIsMessageRecieved] = useState(false);
  const [connectionState, setConnectionState] = useState(
    () =>
      connector.getConnection()?.state ??
      signalR.HubConnectionState.Disconnected
  );
  const [isConnecting, setIsConnecting] = useState(false);
  const currentPathRef = useRef(currentPath);

  useEffect(() => {
    currentPathRef.current = currentPath;
  }, [currentPath]);

  const messageHandler = useCallback((senderId, messageJson) => {
    try {
      const parsed =
        typeof messageJson === "string" ? JSON.parse(messageJson) : messageJson;
      const message = normalizeChatMessage(parsed);

      if (!message.roomId || !message.id) {
        return;
      }

      setNewMessage(message);
      setIsMessageRecieved(true);
    } catch {
      setNewMessage({
        id: Date.now().toString(),
        senderId,
        content: messageJson,
        createdAt: new Date().toISOString(),
        roomId: null,
        attachments: [],
      });
      setIsMessageRecieved(true);
    }
  }, []);

  useEffect(() => {
    messageSubscribers.add(messageHandler);
    return () => {
      messageSubscribers.delete(messageHandler);
    };
  }, [messageHandler]);

  useEffect(() => {
    const onState = (state) => setConnectionState(state);
    stateSubscribers.add(onState);
    return () => {
      stateSubscribers.delete(onState);
    };
  }, []);

  const ensureConnected = useCallback(async () => {
    let connection = connector.getConnection();
    if (!connection) {
      connector.initializeConnection?.();
      connection = connector.getConnection();
    }
    if (!connection) return false;

    bindConnectionLifecycle(
      connection,
      () => isChatHubPath(currentPathRef.current),
      connector
    );

    if (connection.state === signalR.HubConnectionState.Connected) {
      bindSharedHubListeners(connection);
      notifyState(connection.state);
      return true;
    }

    if (connection.state === signalR.HubConnectionState.Connecting) {
      for (let i = 0; i < 50; i += 1) {
        await new Promise((r) => setTimeout(r, 100));
        if (connection.state === signalR.HubConnectionState.Connected) {
          bindSharedHubListeners(connection);
          notifyState(connection.state);
          return true;
        }
      }
      return false;
    }

    setIsConnecting(true);
    try {
      const ok = await connector.startConnection();
      connection = connector.getConnection();
      if (
        ok ||
        connection?.state === signalR.HubConnectionState.Connected
      ) {
        bindSharedHubListeners(connection);
        notifyState(connection.state);
        return true;
      }
      return false;
    } finally {
      setIsConnecting(false);
    }
  }, [connector]);

  // messages + görüşme sayfalarında hub'ı aç
  useEffect(() => {
    if (!isChatHubPath(currentPath)) return undefined;

    let cancelled = false;
    (async () => {
      const ok = await ensureConnected();
      if (cancelled || !ok) return;
      const connection = connector.getConnection();
      if (connection) notifyState(connection.state);
    })();

    return () => {
      cancelled = true;
    };
  }, [currentPath, ensureConnected, connector]);

  const setActiveRoom = useCallback(
    async (roomId) => {
      const ok = await ensureConnected();
      const connection = connector.getConnection();
      if (!ok || !connection) {
        throw new Error("SignalR connection not available");
      }
      await connection.invoke(SET_ACTIVE_ROOM, roomId);
    },
    [connector, ensureConnected]
  );

  const joinRoom = useCallback(
    async (userId, roomIds) => {
      if (!userId || !roomIds?.length) return;
      try {
        const ok = await ensureConnected();
        const connection = connector.getConnection();
        if (!ok || !connection) return;
        await connection.invoke(JOIN_ROOM, { userId, roomIds });
      } catch (err) {
        console.warn("[useHub] JoinRoom failed:", err);
      }
    },
    [connector, ensureConnected]
  );

  const sendMessage = useCallback(
    async (message) => {
      const ok = await ensureConnected();
      const connection = connector.getConnection();
      if (!ok || !connection) {
        throw new Error("SignalR connection not available for sending message");
      }
      const messagePayload = {
        content: message.content || message.textMessage || "",
        attachments: message.attachments || null,
      };
      await connection.invoke(SEND_MESSAGE, messagePayload);
    },
    [connector, ensureConnected]
  );

  useEffect(() => {
    if (newMessage == null || !isMessageRecieved) return undefined;

    if (newMessage.roomId) {
      dispatch(addMessageToRoom(newMessage));
      dispatch(
        apiSlice.util.invalidateTags([
          "MessageRoomUnread",
          "MessageRoomUnreadTotal",
        ])
      );
    }

    const timeoutId = setTimeout(() => {
      setNewMessage(null);
      setIsMessageRecieved(false);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [isMessageRecieved, dispatch, newMessage]);

  const isConnected =
    connectionState === signalR.HubConnectionState.Connected ||
    connector.isConnected();

  return useMemo(
    () => ({
      joinRoom,
      setActiveRoom,
      sendMessage,
      connectionState,
      isConnected,
      isConnecting,
      newMessage,
    }),
    [
      joinRoom,
      setActiveRoom,
      sendMessage,
      connectionState,
      isConnected,
      isConnecting,
      newMessage,
    ]
  );
};

export default useHub;
